import React, { useState, useEffect } from 'react';
import { Editor } from '@monaco-editor/react';
import { X, CheckCircle2, XCircle, Code2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { Problem, Submission } from '@/api/reportApi';

interface DSASubmissionCardProps {
  problem: Problem;
  submission: Submission;
}

const difficultyColors: Record<string, string> = {
  easy: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  hard: 'bg-red-100 text-red-800',
};

const languageLabels: Record<string, string> = {
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  python: 'Python',
  java: 'Java',
  cpp: 'C++',
  c: 'C',
  go: 'Go',
  rust: 'Rust',
};

const DSASubmissionCard: React.FC<DSASubmissionCardProps> = ({
  problem,
  submission,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fontSize, setFontSize] = useState(12);

  // Dynamic font size based on screen size
  useEffect(() => {
    const updateFontSize = () => {
      const width = window.innerWidth;
      if (width >= 1280) {
        setFontSize(14); // xl
      } else if (width >= 1024) {
        setFontSize(13); // lg
      } else {
        setFontSize(12); // md and below
      }
    };

    updateFontSize();
    window.addEventListener('resize', updateFontSize);
    return () => window.removeEventListener('resize', updateFontSize);
  }, []);

  const displayLanguage = submission.language?.trim() || 'plaintext';
  const languageLabel = languageLabels[displayLanguage.toLowerCase()] || displayLanguage;
  const allPassed = submission.passedCount === submission.totalCount && submission.totalCount > 0;

  return (
    <>
      <div className="flex flex-col bg-white rounded-[9px] shadow-sm border border-gray-100 overflow-hidden">
        {/* Problem Section */}
        <div className="flex flex-col gap-3 p-5 md:p-6 border-b border-gray-100">
          {/* Header with title, difficulty badge, and read more */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2 flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-dm-sans text-lg font-semibold text-gray-900">
                  Problem Statement
                </h3>
                <span
                  className={cn(
                    'px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                    difficultyColors[problem.difficulty] || 'bg-gray-100 text-gray-700'
                  )}
                >
                  {problem.difficulty}
                </span>
              </div>
              
              {/* Problem Title */}
              <h4 className="font-dm-sans text-base font-medium text-gray-800">
                {problem.title}
              </h4>
              
              {/* Truncated Description */}
              <p className="font-dm-sans text-sm text-gray-600 line-clamp-3">
                {problem.description}
              </p>
            </div>
          </div>

          {/* Read More Button */}
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            className="self-start font-dm-sans text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors underline underline-offset-2"
          >
            Read full problem...
          </button>
        </div>

        {/* Submitted Code Section */}
        <div className="flex flex-col gap-4 p-5 md:p-6">
          {/* Section Header */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <Code2 className="w-5 h-5 text-gray-500" />
              <h3 className="font-dm-sans text-lg font-semibold text-gray-900">
                Submitted Code
              </h3>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Language Badge */}
              <span className="px-2.5 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                {languageLabel}
              </span>
              
              {/* Test Results Badge */}
              <div
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-medium',
                  allPassed
                    ? 'bg-green-50 text-green-700'
                    : submission.passedCount > 0
                      ? 'bg-yellow-50 text-yellow-700'
                      : 'bg-red-50 text-red-700'
                )}
              >
                {allPassed ? (
                  <CheckCircle2 className="w-4 h-4" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                <span>
                  {submission.passedCount}/{submission.totalCount} Tests Passed
                </span>
              </div>
            </div>
          </div>

          {/* Monaco Editor */}
          <div className="rounded-lg border border-gray-200 overflow-hidden bg-[#FAFAFA]">
            <Editor
              value={submission.code || '// No code submitted'}
              language={displayLanguage}
              theme="light"
              options={{
                readOnly: true,
                domReadOnly: true,
                padding: { top: 16, bottom: 16 },
                minimap: { enabled: false },
                lineNumbers: 'on',
                lineNumbersMinChars: 3,
                lineDecorationsWidth: 0,
                glyphMargin: false,
                folding: true,
                scrollBeyondLastLine: false,
                renderLineHighlight: 'none',
                overviewRulerLanes: 0,
                hideCursorInOverviewRuler: true,
                overviewRulerBorder: false,
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'auto',
                  verticalScrollbarSize: 8,
                  horizontalScrollbarSize: 8,
                },
                fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace",
                fontSize: fontSize,
                lineHeight: 1.6,
                selectionHighlight: false,
                occurrencesHighlight: 'off',
                cursorStyle: 'line-thin',
                cursorBlinking: 'solid',
                renderValidationDecorations: 'off',
                quickSuggestions: false,
                parameterHints: { enabled: false },
                suggestOnTriggerCharacters: false,
                acceptSuggestionOnEnter: 'off',
                tabCompletion: 'off',
                wordBasedSuggestions: 'off',
                selectionClipboard: false,
                contextmenu: false,
                mouseWheelZoom: false,
                links: false,
                hover: { enabled: false },
              }}
              height="350px"
            />
          </div>
        </div>
      </div>

      {/* Full Problem Statement Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent
          className="sm:max-w-[70vw] max-w-[70vw]! w-[70vw] min-h-[50vh] max-h-[80vh] p-0 overflow-hidden rounded-2xl border-none shadow-2xl"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Problem Statement</DialogTitle>
          <div className="relative w-full h-full flex flex-col">
            {/* Custom Floating Close Button */}
            <button
              onClick={() => setIsDialogOpen(false)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/90 hover:bg-gray-100/50 backdrop-blur-sm shadow-sm border border-gray-100 transition-all text-gray-500 hover:text-gray-900"
            >
              <X size={18} />
            </button>

            <div className="flex-1 overflow-y-auto p-8 pt-14">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <span
                  className={cn(
                    'px-2.5 py-0.5 rounded-full text-xs font-medium capitalize',
                    difficultyColors[problem.difficulty] || 'bg-gray-100 text-gray-700'
                  )}
                >
                  {problem.difficulty}
                </span>
                <span className="px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                  {languageLabel}
                </span>
              </div>

              {/* Title */}
              <h2 className="font-dm-sans text-2xl font-semibold text-gray-900 mb-6">
                {problem.title}
              </h2>

              {/* Full Description */}
              <div className="font-dm-sans text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
                {problem.description}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DSASubmissionCard;

